import { useAtom } from "@dbeining/react-atom";
import { Field, Form, Formik } from "formik";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import type {
    TechnicalCompetenceSchemeCreatePayload,
    ITechnicalCompetenceSchemeDetail,
    TechnicalCompetenceSchemeUpdatePayload,
} from "@/customTypings/reference/TechnicalCompetenceScheme";
import { userAtom } from "../../atoms";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import { useViewport } from "../../contexts";
import { IRole } from "../../customTypings/user/Role";
import { TECHNICAL_COMPETENCE_SCHEME_CREATE_CONFLICT } from "@/customTypings/reference/TechnicalCompetenceScheme";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { useReferenceRestoreOnCreateConflict } from "@/hooks/createConflict";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import { technicalCompetenceSchemesService } from "../../services/reference/technicalCompetenceSchemes.service";
import {
    DEFAULT_ACTION_FAILED_MESSAGE,
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    formatReferenceSourceDisplay,
    Role,
    shouldShowFormFieldError,
    arePayloadsEqual,
    editFormBreadcrumbLabel,
    editPageTitle,
    EntityLabels,
    getUserFacingErrorMessage,
    isUnchangedUpdateResponse,
    ListPageTitles,
    loadingPageTitle,
    logErrorForDev,
    NO_CHANGES_TOAST_MESSAGE,
    showToastError,
    showToastSuccess,
    showToastUnchanged,
    buildRecordLifecycleConfirmProps,
    appRoutes,
} from "../../utils";
import {
    buildTcAdminPagedListActions,
    formatTcToastLabel,
    getTcAdminActionSuccessMessage,
    type TcAdminConfirmKind,
} from "../TechnicalCompetenceSchemesListPage/tcAdminListActions";

dayjs.extend(advancedFormat);
dayjs.extend(utc);

function formatTcMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatEffectiveFromDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).format("Do MMMM YYYY");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

type FormValues = TechnicalCompetenceSchemeCreatePayload & { id?: string; isActiveOnCreate?: boolean };

const tcEditValidationSchema = Yup.object({
    id: Yup.string().optional(),
    code: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Scheme value is required.")
        .max(64, "Scheme value must be at most 64 characters."),
    description: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .nullable()
        .transform((v) => v ?? "")
        .optional()
        .max(512, "Description must be at most 512 characters."),
    isActiveOnCreate: Yup.boolean().optional(),
});

type ConfirmKind = TcAdminConfirmKind | null;

const emptyForm: FormValues = {
    code: "",
    description: "",
    isActiveOnCreate: true,
};

function normalizeDescription(value: string | null | undefined): string | null {
    const t = typeof value === "string" ? value.trim() : "";
    return t.length > 0 ? t : null;
}

function buildUpdatePayload(values: FormValues): TechnicalCompetenceSchemeUpdatePayload {
    const t = (s: string) => (typeof s === "string" ? s.trim() : s);
    return { id: values.id!, code: t(values.code), description: normalizeDescription(values.description) };
}

function buildUpdatePayloadFromDetail(detail: ITechnicalCompetenceSchemeDetail): TechnicalCompetenceSchemeUpdatePayload {
    return { id: detail.id, code: detail.code, description: detail.description };
}

const TechnicalCompetenceSchemeEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const isAdmin = currentUser?.identity.roles.some(
        (role: IRole) => role.name === Role.Admin || role.name === Role.SystemAdmin
    );

    const isNew = !id || id === "new";

    const [loading, setLoading] = useState(!isNew);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<FormValues>(emptyForm);
    const [metaActive, setMetaActive] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [source, setSource] = useState("");
    const [sourceVersion, setSourceVersion] = useState("");
    const [effectiveFromUtc, setEffectiveFromUtc] = useState<string | null>(null);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);

    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const savedUpdatePayloadRef = useRef<TechnicalCompetenceSchemeUpdatePayload | null>(null);
    const leaveAfterSaveRef = useRef(false);
    const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);

    const restoreOnCreate = useReferenceRestoreOnCreateConflict({
        deletedRestoreAvailableCode: TECHNICAL_COMPETENCE_SCHEME_CREATE_CONFLICT.deletedRestoreAvailable,
        entityLabel: "technical competence scheme",
        formatSuccessLabel: formatTcToastLabel,
        restoreDeletedWithCreateValues: technicalCompetenceSchemesService.restoreDeletedWithCreateValues,
        onRestored: (detail) => navigate(`/technical-competence-schemes/${detail.id}`),
    });

    const validationSchema = useMemo(() => tcEditValidationSchema, []);

    const formAutoFocusRef = useFormAutoFocus(!loading, [loading, id]);

    const headerMenuItem = useMemo(
        () => ({ isActive: metaActive, isDeleted: metaDeleted }),
        [metaActive, metaDeleted]
    );

    const headerMenuActions = useMemo(
        () => buildTcAdminPagedListActions((kind) => setConfirmKind(kind)),
        []
    );

    const applyTcDetail = (tc: ITechnicalCompetenceSchemeDetail) => {
        setInitialValues({
            id: tc.id,
            code: tc.code,
            description: tc.description ?? "",
        });
        setMetaActive(tc.isActive);
        setMetaDeleted(tc.isDeleted);
        setSource(tc.source);
        setSourceVersion(tc.sourceVersion);
        setEffectiveFromUtc(tc.effectiveFromUtc);
        setCreatedAt(tc.createdAtUtc);
        setUpdatedAt(tc.updatedAtUtc);
        setCreatedByDisplayName(tc.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(tc.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(tc.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(tc.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(tc.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(tc.disabledByDisplayName ?? null);
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(tc);
    };

    useEffect(() => {
        if (isNew || !id) {
            setLoading(false);
            setInitialValues(emptyForm);
            setMetaActive(true);
            setMetaDeleted(false);
            setSource("");
            setSourceVersion("");
            setEffectiveFromUtc(null);
            setCreatedByDisplayName(null);
            setLastUpdatedByDisplayName(null);
            setDeletedAuditAtUtc(null);
            setDeletedByDisplayName(null);
            setDisabledAuditAtUtc(null);
            setDisabledByDisplayName(null);
            return;
        }
        setLoading(true);
        setLoadError(null);
        technicalCompetenceSchemesService
            .getById(id)
            .then(applyTcDetail)
            .catch((e: unknown) => {
                logErrorForDev("TechnicalCompetenceSchemeEditPage.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load technical competence scheme."));
            })
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.technicalCompetenceScheme,
                initialValues.code || `this ${EntityLabels.technicalCompetenceScheme.toLowerCase()}`
            ),
        [confirmKind, initialValues.code]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || isNew || !kind) return;
        const op =
            kind === "disable"
                ? technicalCompetenceSchemesService.disable(id)
                : kind === "enable"
                  ? technicalCompetenceSchemesService.enable(id)
                  : kind === "softDelete"
                    ? technicalCompetenceSchemesService.softDelete(id)
                    : technicalCompetenceSchemesService.restore(id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getTcAdminActionSuccessMessage(kind, initialValues.code));
            if (kind === "softDelete") {
                navigate("/technical-competence-schemes", { replace: true });
                return;
            }
            return technicalCompetenceSchemesService.getById(id).then(applyTcDetail);
        }).catch((e: unknown) => {
            setConfirmKind(null);
            showToastError(getUserFacingErrorMessage(e, DEFAULT_ACTION_FAILED_MESSAGE));
        });
    };

    if (!isAdmin) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage technical competence schemes.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.technicalCompetenceScheme)} />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError}</Alert>
                <Link to="/technical-competence-schemes">Back to technical competence schemes</Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.technicalCompetenceScheme, isNew)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: EntityLabels.referenceData, to: appRoutes.codes },
                    { text: ListPageTitles.technicalCompetenceSchemes, to: "/technical-competence-schemes" },
                    { text: editFormBreadcrumbLabel(isNew) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{
                    borderBottom: "2px solid var(--primary)",
                    paddingBottom: "0.5rem",
                }}
            >
                <h1
                    className="mb-0 flex-grow-1 min-w-0"
                    style={{ borderBottom: "none", paddingBottom: 0 }}
                >
                    {editPageTitle(EntityLabels.technicalCompetenceScheme, isNew)}
                </h1>
                {isNarrowScreen && !isNew && Boolean(initialValues.id?.trim()) && (
                    <EllipsisActionMenu
                        id="tc-edit-actions-dropdown"
                        ariaLabel="Technical competence scheme actions"
                        actions={headerMenuActions}
                        item={headerMenuItem}
                    />
                )}
            </div>

            {!isNew && (
                <RecordAuditMetaBar
                    formatDate={formatTcMetaDate}
                    formatByLine={formatByLine}
                    createdAt={createdAt}
                    createdByDisplayName={createdByDisplayName}
                    updatedAt={updatedAt}
                    lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                    isDeleted={metaDeleted}
                    isEnabled={metaActive}
                    deletedAuditAtUtc={deletedAuditAtUtc}
                    deletedByDisplayName={deletedByDisplayName}
                    disabledAuditAtUtc={disabledAuditAtUtc}
                    disabledByDisplayName={disabledByDisplayName}
                />
            )}

            <Formik<FormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur={false}
                onSubmit={async (values, { resetForm }) => {
                    if (metaDeleted) return;
                    saveOutcomeRef.current = null;
                    setLiveValidationAfterSubmit(true);
                    try {
                        const t = (s: string) => (typeof s === "string" ? s.trim() : s);
                        const code = t(values.code);
                        const description = normalizeDescription(values.description);
                        if (isNew) {
                            try {
                                const created = await technicalCompetenceSchemesService.create({
                                    code,
                                    description,
                                    isActive: values.isActiveOnCreate,
                                });
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess(`${formatTcToastLabel(code)} created successfully!`);
                                navigate(`/technical-competence-schemes/${created.id}`);
                            } catch (err: unknown) {
                                if (restoreOnCreate.handleCreateError(err)) {
                                    restoreOnCreate.setPendingRestoreForm({
                                        description: description ?? "",
                                        isActive: values.isActiveOnCreate ?? true,
                                    });
                                    setLiveValidationAfterSubmit(false);
                                    return;
                                }
                                showToastError(getUserFacingErrorMessage(err, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE));
                            }
                        } else if (values.id) {
                            const updatePayload = buildUpdatePayload(values);
                            if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                                setLiveValidationAfterSubmit(false);
                                saveOutcomeRef.current = "unchanged";
                                showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                                if (leaveAfterSaveRef.current) return;
                                return;
                            }
                            const result = await technicalCompetenceSchemesService.update(updatePayload);
                            if (isUnchangedUpdateResponse(result)) {
                                setLiveValidationAfterSubmit(false);
                                saveOutcomeRef.current = "unchanged";
                                showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                                if (leaveAfterSaveRef.current) return;
                                return;
                            }
                            setLiveValidationAfterSubmit(false);
                            saveOutcomeRef.current = "success";
                            savedUpdatePayloadRef.current = updatePayload;
                            showToastSuccess(`${formatTcToastLabel(code)} updated successfully!`);
                            resetForm({ values });
                            if (leaveAfterSaveRef.current) return;
                            navigate("/technical-competence-schemes");
                        }
                    } catch (e: unknown) {
                        saveOutcomeRef.current = "failure";
                        showToastError(getUserFacingErrorMessage(e, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE));
                    }
                }}
            >
                {({ isSubmitting, values, errors, touched, submitCount, handleSubmit, validateForm, setTouched }) => {
                    const hasPersistedRecord = Boolean(values.id?.trim());
                    const showErr = (field: keyof FormValues) =>
                        shouldShowFormFieldError(
                            errors,
                            field as keyof typeof errors,
                            hasPersistedRecord,
                            submitCount,
                            liveValidationAfterSubmit,
                            touched
                        );
                    const form = (
                        <Form
                            ref={formAutoFocusRef}
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            {metaDeleted && (
                                <Alert variant="warning" className="mb-3">
                                    This scheme is deleted. Restore it before you make any changes.
                                </Alert>
                            )}

                            <Row className="g-3">
                                {!isNew && (
                                    <>
                                        <Col md={4}>
                                            <label className="form-label mb-0">Source</label>
                                            <div className="form-control-plaintext py-0">
                                                {formatReferenceSourceDisplay(source) || "-"}
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <label className="form-label mb-0">Source version</label>
                                            <div className="form-control-plaintext py-0">{sourceVersion || "-"}</div>
                                        </Col>
                                        <Col md={4}>
                                            <label className="form-label mb-0">Effective from</label>
                                            <div className="form-control-plaintext py-0">
                                                {effectiveFromUtc ? formatEffectiveFromDate(effectiveFromUtc) : "-"}
                                            </div>
                                        </Col>
                                    </>
                                )}
                                <Col md={6}>
                                    <label className="form-label" htmlFor="code">
                                        Scheme value <span className="text-danger" aria-hidden="true">*</span>
                                        <span className="visually-hidden"> (required)</span>
                                    </label>
                                    <Field
                                        id="code"
                                        name="code"
                                        className={`form-control font-monospace${showErr("code") ? " is-invalid" : ""}`}
                                        disabled={metaDeleted}
                                        aria-required
                                        aria-invalid={showErr("code")}
                                    />
                                    {showErr("code") && (
                                        <div className="invalid-feedback d-block">{errors.code}</div>
                                    )}
                                </Col>
                                <Col xs={12}>
                                    <label className="form-label" htmlFor="description">
                                        Description
                                    </label>
                                    <Field
                                        id="description"
                                        name="description"
                                        as="textarea"
                                        rows={3}
                                        className={`form-control${showErr("description") ? " is-invalid" : ""}`}
                                        disabled={metaDeleted}
                                        aria-invalid={showErr("description")}
                                    />
                                    {showErr("description") && (
                                        <div className="invalid-feedback d-block">{errors.description}</div>
                                    )}
                                </Col>
                                {isNew && (
                                    <Col xs={12}>
                                        <div className="form-check">
                                            <Field
                                                id="isActiveOnCreate"
                                                name="isActiveOnCreate"
                                                type="checkbox"
                                                className="form-check-input"
                                            />
                                            <label className="form-check-label" htmlFor="isActiveOnCreate">
                                                Active on create
                                            </label>
                                        </div>
                                    </Col>
                                )}
                            </Row>

                            <EditFormSplitFooter
                                start={
                                    shouldShowEditFormLifecycleActions(isNew, values.id, isNarrowScreen) ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            <EditFormLifecycleActions
                                                metaDeleted={metaDeleted}
                                                metaEnabled={metaActive}
                                                onAction={setConfirmKind}
                                            />
                                        </div>
                                    ) : undefined
                                }
                            >
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={metaDeleted}
                                    readOnlyForm={metaDeleted}
                                    isNewRecord={isNew}
                                    newRecordLabel={EntityLabels.technicalCompetenceScheme}
                                    enableSaveBeforeLeave={!isNew}
                                    leaveAfterSaveRef={leaveAfterSaveRef}
                                    saveOutcomeRef={saveOutcomeRef}
                                    onCancel={() => navigate("/technical-competence-schemes")}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );

                    return (
                        <FormikUnsavedChangesGuard
                            enableRouteBlocking
                            readOnlyForm={metaDeleted}
                            isNewRecord={isNew}
                            newRecordLabel={EntityLabels.technicalCompetenceScheme}
                            enableSaveBeforeLeave={!isNew}
                            leaveAfterSaveRef={leaveAfterSaveRef}
                            saveOutcomeRef={saveOutcomeRef}
                        >
                            {form}
                        </FormikUnsavedChangesGuard>
                    );
                }}
            </Formik>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />

            <ConfirmationModel
                show={restoreOnCreate.restoreModalProps.show}
                onClose={restoreOnCreate.confirmRestore}
                title={restoreOnCreate.restoreModalProps.title}
                body={restoreOnCreate.restoreModalProps.body}
                buttonSuccessVariant="primary"
                buttonSuccessText={restoreOnCreate.restoreModalProps.buttonSuccessText}
                buttonCancelText={restoreOnCreate.restoreModalProps.buttonCancelText}
            />
        </Container>
    );
};

export { TechnicalCompetenceSchemeEditPage };
